import React, { memo, useMemo } from 'react';
import { MonthlyDateQuickEditHost } from './MonthlyDateQuickEditHost';
import { formatMonthlyDateBadgeLabel } from '../utils/formatMonthlyDateBadgeLabel';
import { getMonthlyBadgeVariant } from '../utils/getMonthlyBadgeVariant';
import { isEventLane, isKeyDateLane } from '../utils/monthlyItemIconUtils';
import styles from '../MonthlyRoadmap.module.scss';
import type { MonthlyTimelineItem } from '../utils/types';

interface MonthlyDateBadgeProps {
  item: MonthlyTimelineItem;
  laneLabel: string;
  isDateClicked?: boolean;
  setIsDateClicked?: (isDateClicked: boolean) => void;
  useCompletedStyle?: boolean;
  onClick?: () => void;
}

export const MonthlyDateBadge = memo(
  ({
    item,
    laneLabel,
    isDateClicked,
    setIsDateClicked,
    useCompletedStyle = false,
    onClick
  }: MonthlyDateBadgeProps) => {
    const displayLabel = useMemo(
      () => formatMonthlyDateBadgeLabel(item, laneLabel),
      [item, laneLabel]
    );

    const { style, isOverdue, isCompleted } = useMemo(
      () => getMonthlyBadgeVariant(item, laneLabel),
      [item, laneLabel]
    );

    const badgeClassName = useMemo(() => {
      const showCompletedStyle = isCompleted || useCompletedStyle;

      if (isOverdue && !showCompletedStyle) {
        return style === 'filled'
          ? styles.monthlyDateBadgeFilledOverdue
          : styles.monthlyDateBadgeOverdue;
      }

      if (showCompletedStyle) {
        return style === 'filled'
          ? styles.monthlyDateBadgeFilledCompleted
          : styles.monthlyDateBadgeOutlineCompleted;
      }

      return style === 'filled'
        ? styles.monthlyDateBadgeFilled
        : styles.monthlyDateBadgeOutline;
    }, [isCompleted, isOverdue, style, useCompletedStyle]);

    const badge = (
      <span
        className={`${styles.monthlyDateBadge} ${badgeClassName} ${
          onClick ? styles.monthlyDateBadgeClickable : ''
        }`}
        {...(onClick
          ? {
              role: 'button',
              tabIndex: 0,
              onClick,
              onKeyDown: (event: React.KeyboardEvent<HTMLSpanElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClick();
                }
              }
            }
          : {})}
      >
        {displayLabel}
      </span>
    );

    if (isEventLane(laneLabel) || isKeyDateLane(laneLabel)) {
      return badge;
    }

    return (
      <MonthlyDateQuickEditHost
        item={item}
        laneLabel={laneLabel}
        isDateClicked={isDateClicked}
        setIsDateClicked={setIsDateClicked}
      >
        {badge}
      </MonthlyDateQuickEditHost>
    );
  }
);
